import { getActiveCaregiverId, getActiveOfficeId } from '@lib/activeOffice';
import { getDefaulSocketConfig } from '@src/const/socketConfig';
import { useAuthStore } from '@stores/authStore';
import { SocketEvent } from '@therame/shared';
import { defineStore } from 'pinia';
import { ReplaySubject } from 'rxjs';
import { Socket, io } from 'socket.io-client';
import { ref, type Ref } from 'vue';

type OfficeAchievementSocketStore = {
  socket: Ref<Socket | undefined>;
  socketEndpoint: string;
  connect: () => Promise<void>;
  officeAchievementTrigger: ReplaySubject<SocketEvent<'office-achievement-received'>>;
  disconnectSocket: () => void;
};

export const useOfficeAchievementSocketStore = defineStore(
  'officeAchievementSocket',
  (): OfficeAchievementSocketStore => {
    const authStore = useAuthStore();
    const socketEndpoint = (import.meta.env.VITE_WS_ROOT as string) + '/office-achievement';
    const socket = ref<Socket | undefined>();
    const officeAchievementTrigger = new ReplaySubject<
      SocketEvent<'office-achievement-received'>
    >();
    const reconnectInterval = ref<ReturnType<typeof setInterval>>();

    const disconnectSocket = () => {
      socket.value?.disconnect();
      clearInterval(reconnectInterval.value);
    };

    const connectSocket = async () => {
      return Promise.race([
        new Promise((resolve) => {
          setTimeout(() => {
            resolve(false);
          }, 1000);
        }),
        new Promise<boolean>((resolve) => {
          socket.value = io(
            socketEndpoint,
            getDefaulSocketConfig(
              authStore.authContext?.token,
              getActiveOfficeId(),
              getActiveCaregiverId(),
            ),
          );
          socket.value.on('connect', () => {
            resolve(true);
          });
          socket.value?.on(
            'office-achievement-received',
            (event: SocketEvent<'office-achievement-received'>) => {
              const officeAchievementAlert = event.props.officeAchievementAlert;
              if (officeAchievementAlert) {
                officeAchievementTrigger.next(event);
              }
            },
          );
          socket.value.connect();
        }),
      ]);
    };

    const connect = async () => {
      const connected = await connectSocket();
      if (!connected) {
        clearInterval(reconnectInterval.value);
        reconnectInterval.value = setInterval(async () => {
          const connected = await connectSocket();
          if (connected) {
            clearInterval(reconnectInterval.value);
          }
        }, 1000);
      }
    };

    const store: OfficeAchievementSocketStore = {
      socket,
      socketEndpoint,
      connect,
      officeAchievementTrigger,
      disconnectSocket,
    };
    return store;
  },
);
